export interface Asset {
  sys: {
    id: string;
  };
  fields: {
    file: {
      url: string;
    };
  };
}

interface ReadTime {
  minutes: number;
}

export interface Article {
  id: string;
  fields: {
    title: string;
    slug: string;
    url: string;
    tag: string;
    tagUrl: string;
    coverImage: string | null;
    excerpt: string;
    date: string;
    author: string;
    readTime?: string;
  };
}

interface GraphQLCollection<T> {
  items: T[];
}

interface ArticleItem {
  sys: { id: string };
  title: string;
  slug: string;
  date: string;
  coverImage?: { url: string };
  author?: { fullName: string };
  excerpt: string;
  tags: { tags: { id: string; name: string }[] };
  readTime: ReadTime;
}

interface CaseStudyItem {
  sys: { id: string };
  title: string;
  slug: string;
  date: string;
  coverImage?: { url: string };
  excerpt: string;
  tags: { tags: { id: string; name: string }[] };
}

interface VideoItem {
  sys: { id: string };
  title: string;
  slug: string;
  date: string;
  excerpt: string;
  video?: {
    items: {
      id: number;
      duration: number;
      hashed_id: string;
      thumbnail?: {
        url: string;
        width: number;
        height: number;
      };
    }[];
  };
  tags: { tags: { id: string; name: string }[] };
}

interface LatestContentData {
  articleCollection?: GraphQLCollection<ArticleItem>;
  caseStudyCollection?: GraphQLCollection<CaseStudyItem>;
  videoCollection?: GraphQLCollection<VideoItem>;
}

/**
 * Builds the GraphQL query string and variables for fetching the latest content (articles, case studies, videos).
 *
 * @param tags - Optional tag(s) to filter by. If a non-empty string, it will be treated as a single tag.
 *               If an array, each non-empty trimmed string will be used. Otherwise, no tag filter is applied.
 * @param limit - How many items to fetch per collection.
 * @param skipFirst - How many items to skip per collection (note: global skip/limit will be applied after merging).
 * @param justArticles - If true, only fetch articles; videos and case studies are skipped.
 * @returns An object containing the query string and variables.
 */
export function buildLatestArticlesQuery(
  tags: string | string[] | null = null,
  limit: number,
  skipFirst: number,
  justArticles: boolean = false,
): {
  query: string;
  variables: {
    justArticles: boolean;
    limit: number;
    skipFirst: number;
    tags?: string[];
  };
} {
  let normalizedTags: string[] = [];
  if (typeof tags === "string" && tags.trim()) {
    normalizedTags = [tags.trim()];
  } else if (Array.isArray(tags)) {
    normalizedTags = tags.map((t) => t.trim()).filter((t) => t !== "");
  }
  const query = `
    query GetContentfulLatest($justArticles: Boolean = false, $tags: [String], $limit: Int = 10, $skipFirst: Int = 0) {
      articleCollection(
        order: date_DESC,
        limit: $limit,
        skip: $skipFirst,
        where: { contentfulMetadata: { tags_exists: true, tags: { id_contains_some: $tags } } },
      ) {
        items {
          sys { id }
          title
          slug
          date
          coverImage { url }
          author { fullName }
          excerpt
          tags: contentfulMetadata { tags { id name } }
          readTime
        }
      }
      caseStudyCollection(
        order: date_DESC,
        limit: $limit,
        skip: $skipFirst,
        where: { contentfulMetadata: { tags_exists: true, tags: { id_contains_some: $tags } } },
      ) @skip(if: $justArticles) {
        items {
          sys { id }
          title
          slug
          date
          coverImage { url }
          excerpt: summary
          tags: contentfulMetadata { tags { id name } }
        }
      }
      videoCollection(
        order: date_DESC,
        limit: $limit,
        skip: $skipFirst,
        where: { contentfulMetadata: { tags_exists: true, tags: { id_contains_some: $tags } } },
      ) @skip(if: $justArticles) {
        items {
          sys { id }
          title
          slug
          date
          excerpt
          video
          tags: contentfulMetadata { tags { id name } }
        }
      }
    }`;
  const variables: {
    justArticles: boolean;
    limit: number;
    skipFirst: number;
    tags?: string[];
  } = {
    justArticles,
    limit,
    skipFirst,
  };
  if (normalizedTags && normalizedTags.length > 0) {
    variables.tags = normalizedTags;
  }
  return { query, variables };
}

/**
 * Merges the raw results from multiple collections (articles, case studies, videos) into a single array of Articles.
 * It enriches each entry with a computed URL based on its collection type, sorts them by date descending,
 * and applies global skip and limit.
 *
 * @param data - The raw data returned from the GraphQL query.
 * @param globalSkip - The number of items to skip from the merged sorted list.
 * @param globalLimit - The maximum number of items to return from the merged sorted list.
 * @returns An array of Articles.
 */
export function mergeCollectionsIntoArticles(
  data: LatestContentData,
  globalSkip: number,
  globalLimit: number,
): Article[] {
  const articlesRaw = data.articleCollection?.items ?? [];
  const caseStudiesRaw = data.caseStudyCollection?.items ?? [];
  const videosRaw = data.videoCollection?.items ?? [];

  type MappedItem = { article: Article; timestamp: number };
  const mappedItems: MappedItem[] = [];

  // Helper to truncate excerpt if needed
  const processExcerpt = (excerpt: string): string => {
    if (!excerpt) return "";
    if (excerpt.length > 140) {
      const truncated = excerpt.slice(0, 140);
      const lastSpace = truncated.lastIndexOf(" ");
      return truncated.slice(0, lastSpace) + "...";
    }
    return excerpt;
  };

  // Map articles
  articlesRaw.forEach((item) => {
    const ts = new Date(item.date).getTime();
    const article: Article = {
      id: item.sys?.id || "",
      fields: {
        title: item.title,
        slug: item.slug,
        url: `/learn/${item.slug}`,
        tag: item.tags?.tags?.[0]?.name || "",
        tagUrl: item.tags?.tags?.[0]?.id
          ? `/topic?series=${item.tags.tags[0].id}`
          : "",
        coverImage: item.coverImage ? item.coverImage.url : null,
        excerpt: processExcerpt(item.excerpt),
        date: new Date(item.date).toLocaleDateString(undefined, {
          year: "2-digit",
          month: "short",
          day: "numeric",
        }),
        author: item.author ? item.author.fullName : "",
        readTime: `${Math.ceil(item.readTime?.minutes ?? 3)} min`,
      },
    };
    mappedItems.push({ article, timestamp: ts });
  });

  // Map case studies
  caseStudiesRaw.forEach((item) => {
    const ts = new Date(item.date).getTime();
    const article: Article = {
      id: item.sys?.id || "",
      fields: {
        title: item.title,
        slug: item.slug,
        url: `/learn/case-study/${item.slug}`,
        tag: item.tags?.tags?.[0]?.name || "",
        tagUrl: item.tags?.tags?.[0]?.id
          ? `/topic?series=${item.tags.tags[0].id}`
          : "",
        coverImage: item.coverImage ? item.coverImage.url : null,
        excerpt: processExcerpt(item.excerpt),
        date: new Date(item.date).toLocaleDateString(undefined, {
          year: "2-digit",
          month: "short",
          day: "numeric",
        }),
        author: "",
      },
    };
    mappedItems.push({ article, timestamp: ts });
  });

  // Map videos with updated coverImage extraction
  videosRaw.forEach((item) => {
    const ts = new Date(item.date).getTime();
    // Extract coverImage from video thumbnail if available
    const coverImage = item.video?.items?.[0]?.thumbnail?.url || null;
    const article: Article = {
      id: item.sys?.id || "",
      fields: {
        title: item.title,
        slug: item.slug,
        url: `/learn/videos/${item.slug}`,
        tag: item.tags?.tags?.[0]?.name || "",
        tagUrl: item.tags?.tags?.[0]?.id
          ? `/topic?series=${item.tags.tags[0].id}`
          : "",
        coverImage: coverImage,
        excerpt: processExcerpt(item.excerpt),
        date: new Date(item.date).toLocaleDateString(undefined, {
          year: "2-digit",
          month: "short",
          day: "numeric",
        }),
        author: "",
      },
    };
    mappedItems.push({ article, timestamp: ts });
  });

  // Sort by timestamp descending and apply global skip/limit
  mappedItems.sort((a, b) => b.timestamp - a.timestamp);
  const sliced = mappedItems.slice(globalSkip, globalSkip + globalLimit);
  return sliced.map((item) => item.article);
}
/**
 * Fetches the latest content (articles, case studies, videos) from Contentful using the GraphQL API.
 * Aggregates the results from all collections into a single sorted array of Articles, applying global skip and limit.
 *
 * @param spaceId - The Contentful space ID.
 * @param environment - The Contentful environment.
 * @param accessToken - The Contentful access token.
 * @param tags - Optional tag(s) to filter by. If a non-empty string, it will be treated as a single tag.
 *               If an array, each non-empty trimmed string will be used. Otherwise, no tag filter is applied.
 * @param limit - The maximum number of items to return (after merging).
 * @param skipFirst - The number of items to skip from the merged sorted list.
 * @param justArticles - If true, only fetch articles; videos and case studies are skipped.
 * @returns A promise that resolves to an array of Articles.
 */
export async function fetchLatestArticles(
  spaceId: string,
  environment: string,
  accessToken: string,
  tags: string | string[] | null = null,
  limit = 3,
  skipFirst = 0,
  justArticles = false,
): Promise<Article[]> {
  const { query, variables } = buildLatestArticlesQuery(
    tags,
    limit,
    skipFirst,
    justArticles,
  );
  const url = `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const { data, errors } = await response.json();
  if (errors) {
    throw new Error(
      `Contentful GraphQL query error: ${JSON.stringify(errors)}`,
    );
  }
  // Merge collections with global skip and limit applied
  const mergedArticles = mergeCollectionsIntoArticles(data, skipFirst, limit);
  return mergedArticles;
}

export interface RichContent {
  json: RichTextDocument;
  links: RichContentLinks;
}

// Represents the rich text JSON document.
// This is a simplified version of Contentful's rich text document structure.
interface RichTextDocument {
  nodeType: string;
  data: any;
  content: RichTextNode[];
}

export interface RichTextNode {
  nodeType: string;
  data: any;
  content?: RichTextNode[];
  value?: string;
  marks?: { type: string }[];
}

// Represents the links for both entries and assets within richContent
interface RichContentLinks {
  entries: {
    block: RichContentEntryBlock[];
  };
  assets: {
    block: RichContentAssetBlock[];
  };
}

// Represents a linked entry block (e.g., CallToAction)
interface RichContentEntryBlock {
  sys: {
    id: string;
  };
  __typename: string;
  // Fields for the CallToAction type
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonUrl?: string;
}

// Represents a linked asset block (e.g., images)
interface RichContentAssetBlock {
  sys: {
    id: string;
  };
  url: string;
  title?: string;
  description?: string;
}

export interface ContentfulArticle {
  slug: string;
  title: string;
  date: Date;
  excerpt?: string;
  coverImage?: {
    url: string;
    title?: string;
    description?: string;
  };
  author?: {
    fullName: string;
    picture?: {
      url: string;
    };
  };
  contentfulMetadata: {
    tags: { id: string; name: string }[];
  };
  richContent?: RichContent;
  readTime?: ReadTime;
}

export type ContentfulFetchFunction<T> = (
  spaceId: string,
  environment: string,
  accessToken: string,
  slug: string,
) => Promise<T>;

/**
 * Fetches a single article from Contentful by its slug, including all associated linked entries and assets.
 *
 * @param spaceId - The Contentful space ID.
 * @param environment - The Contentful environment.
 * @param accessToken - The Contentful access token.
 * @param slug - The slug of the article to fetch.
 * @returns A promise that resolves to an Article object or null if not found.
 */
export const fetchArticleBySlug: ContentfulFetchFunction<
  ContentfulArticle
> = async (spaceId, environment, accessToken, slug) => {
  // Construct the GraphQL API URL
  const url = `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`;

  // Define the GraphQL query
  const query = `
    query {
        articleCollection(where: { slug: "${slug}" }, limit: 1) {
            items {
                contentfulMetadata {
                    tags {
                        id
                        name
                    }
                }
                slug
                title
                coverImage {
                    url
                }
                date
                author {
                    fullName
                }
                excerpt
                richContent {
                    json
                    links {
                        entries {
                            block {
                                sys {
                                    id
                                }
                                __typename
                                ... on CallToAction {
                                    title
                                    subtitle
                                    buttonText
                                    buttonUrl
                                }
                            }
                        }
                        assets {
                            block {
                                sys {
                                    id
                                }
                                url
                                title
                                description
                            }
                        }
                    }
                }
                readTime
            }
        }
    }
  `;

  // Perform the GraphQL request
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ query }),
  });

  // Throw an error if the request fails
  if (!response.ok) {
    throw new Error(
      `Failed to fetch article: ${response.status} ${response.statusText}`,
    );
  }

  // Parse JSON response
  const data = await response.json();

  // Extract the article data
  return data?.data?.articleCollection?.items?.[0] || null;
};

export async function fetchContentfulTags(
  spaceId: string,
  environment: string,
  accessToken: string,
): Promise<Record<string, string>> {
  const url = `https://cdn.contentful.com/spaces/${spaceId}/environments/${environment}/tags`;
  try {
    // Fetch the tags
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      throw new Error(
        `Failed to fetch tags: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    const tags: Record<string, string> = {};
    for (const { name, sys: { id } = {} } of data.items || []) {
      if (!id) continue; // skip if no ID (shouldn't really happen for valid tags?)
      // Use the tag's name if available; otherwise, fall back to the ID
      tags[id] = name && name.trim() !== "" ? name : id;
    }
    return tags;
  } catch (err) {
    console.error(`Error fetching tags from Contentful:`, err);
    throw err; // rethrow the error after logging
  }
}

export interface ContentfulCaseStudy {
  slug: string;
  title: string;
  summary: string;
  date: Date;
  keyFactsCollection: {
    items: { value: string; label: string }[];
  };
  coverImage?: {
    url: string;
    title?: string;
    description?: string;
  };
  contentfulMetadata: {
    tags: { id: string; name: string }[];
  };
  richContent: {
    json: RichTextDocument;
    links: RichContentLinks;
  };
}

export const fetchCaseStudyBySlug: ContentfulFetchFunction<
  ContentfulCaseStudy | null
> = async (spaceId, environment, accessToken, slug) => {
  const query = `
    query {
      caseStudyCollection(where: { slug: "${slug}" }, limit: 1) {
        items {
          contentfulMetadata {
            tags {
              id
              name
            }
          }
          slug
          title
          summary
          coverImage {
            url
          }
          keyFactsCollection(skip: 0, limit: 4) {
            items {
              value
              label
            }
          }
          date
          richContent: content {
            json
            links {
              entries {
                block {
                  sys {
                    id
                  }
                  __typename
                  ... on CommunityAsk {
                    icon {
                      url
                    }
                    title
                    description
                    cta
                    link
                  }
                  ... on Quote {
                    text
                    fullName
                    title
                    headshot {
                      url
                    }
                  }
                }
              }
              assets {
                block {
                  sys {
                    id
                  }
                  url
                  title
                  description
                }
              }
            }
          }
        }
      }
    }
  `;

  const url = `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch case study: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return data?.data?.caseStudyCollection?.items?.[0] || null;
};

export interface ContentfulVideo {
  slug: string;
  title: string;
  date: Date;
  excerpt: string;
  video: {
    items: {
      id: number;
      duration: number;
      hashed_id: string;
      thumbnail: {
        url: string;
        width: number;
        height: number;
      };
    }[];
  };
  contentfulMetadata: {
    tags: { id: string; name: string }[];
  };
  richContent: {
    json: RichTextDocument;
    links: RichContentLinks;
  };
}

export const fetchVideoBySlug: ContentfulFetchFunction<
  ContentfulVideo | null
> = async (spaceId, environment, accessToken, slug) => {
  const query = `
    query {
      videoCollection(where: { slug: "${slug}" }, limit: 1) {
          items {
              contentfulMetadata {
                  tags {
                      id
                      name
                  }
              }
              slug
              title
              date
              excerpt
              richContent {
                  json
                  links {
                      entries {
                          block {
                              sys {
                                  id
                              }
                              __typename
                              ... on CallToAction {
                                  title
                                  subtitle
                                  buttonText
                                  buttonUrl
                              }
                          }
                      }
                      assets {
                          block {
                              sys {
                                  id
                              }
                              url
                              title
                              description
                          }
                      }
                  }
              }
              video
          }
      }
    }
  `;

  const url = `https://graphql.contentful.com/content/v1/spaces/${spaceId}/environments/${environment}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch video: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return data?.data?.videoCollection?.items?.[0] || null;
};
